import type { ComparisonConfig } from './ComparisonPage';

/* ========================================================================== */
/*  HotelStack.io vs Oracle OPERA                                             */
/* ========================================================================== */

const HOTELSTACK_VS_OPERA: ComparisonConfig = {
  slug: 'hotelstack-vs-opera',
  competitorName: 'Oracle OPERA',
  heroTitle: 'HotelStack.io vs Oracle OPERA',
  heroSubtitle: 'Oracle OPERA is the legacy industry standard — built decades ago and still carrying enterprise-era complexity and pricing. HotelStack.io delivers a modern, cloud-native alternative with more modules, simpler pricing, and zero infrastructure overhead.',
  overviewComparison: [
    { label: 'Architecture', hotelstack: 'Cloud-Native SaaS', competitor: 'Legacy On-Premise / Cloud' },
    { label: 'Pricing', hotelstack: 'From $49/mo', competitor: '$500+/mo + Licenses' },
    { label: 'Implementation', hotelstack: 'Free — 48hr Onboarding', competitor: '3-6 Month Implementation' },
    { label: 'Total Modules', hotelstack: '36 Built-In', competitor: '20+ (Separate Licenses)' },
    { label: 'F&B / POS', hotelstack: 'Built-In', competitor: 'Separate OPERA POS License' },
    { label: 'Target Segment', hotelstack: 'SMB to Enterprise', competitor: 'Enterprise Only' },
  ],
  featureTable: [
    {
      category: 'AI & Intelligence',
      features: [
        { name: 'AI Dynamic Pricing', hotelstack: true, competitor: false },
        { name: 'AI Demand Forecasting (30/60/90 day)', hotelstack: true, competitor: false },
        { name: 'AI Review Response Generator', hotelstack: true, competitor: false },
        { name: 'AI Guest Concierge (Multilingual)', hotelstack: true, competitor: false },
        { name: 'AI Sentiment Analysis', hotelstack: true, competitor: 'Limited' },
        { name: 'Predictive Maintenance', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'Core PMS',
      features: [
        { name: 'Reservation Management', hotelstack: true, competitor: true },
        { name: 'Visual Room Board', hotelstack: true, competitor: true },
        { name: 'Night Audit Wizard', hotelstack: true, competitor: true },
        { name: 'Guest Folio Management', hotelstack: true, competitor: true },
        { name: 'Group Bookings', hotelstack: true, competitor: true },
        { name: 'Online Check-In Portal', hotelstack: true, competitor: 'Add-On' },
      ],
    },
    {
      category: 'Cloud & Accessibility',
      features: [
        { name: 'True Cloud-Native', hotelstack: true, competitor: 'Hybrid' },
        { name: 'Zero Server Maintenance', hotelstack: true, competitor: false },
        { name: 'Real-Time Multi-Device Sync', hotelstack: true, competitor: 'Limited' },
        { name: 'Modern Responsive UI', hotelstack: true, competitor: false },
        { name: 'Automatic Updates', hotelstack: true, competitor: false },
        { name: 'No IT Staff Required', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'Revenue Management',
      features: [
        { name: 'Dynamic Pricing Rules', hotelstack: true, competitor: true },
        { name: 'What-If Revenue Simulator', hotelstack: true, competitor: false },
        { name: 'Competitor Rate Monitor', hotelstack: true, competitor: 'Via Oracle BI' },
        { name: 'Demand Forecasting', hotelstack: true, competitor: true },
        { name: 'RevPAR Analytics', hotelstack: true, competitor: true },
      ],
    },
    {
      category: 'F&B & Operations',
      features: [
        { name: 'POS Terminal', hotelstack: true, competitor: 'Separate License' },
        { name: 'Room Service Module', hotelstack: true, competitor: 'Separate License' },
        { name: 'Kitchen Display System', hotelstack: true, competitor: false },
        { name: 'Staff Scheduling', hotelstack: true, competitor: false },
        { name: 'Maintenance Work Orders', hotelstack: true, competitor: 'Via Integration' },
      ],
    },
    {
      category: 'Cost & Onboarding',
      features: [
        { name: 'Free Onboarding', hotelstack: true, competitor: false },
        { name: 'Free Data Migration', hotelstack: true, competitor: false },
        { name: 'No Server Costs', hotelstack: true, competitor: false },
        { name: 'No License Fees', hotelstack: true, competitor: false },
        { name: 'Regional Currency Pricing', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'Resilience & Offline',
      features: [
        { name: 'Offline-Capable PWA', hotelstack: true, competitor: false },
        { name: 'Offline Mutation Queue', hotelstack: true, competitor: false },
        { name: 'Background Sync on Reconnect', hotelstack: true, competitor: false },
        { name: 'Conflict-Aware Reconciliation', hotelstack: true, competitor: false },
        { name: 'Installable Front Desk', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'Corporate & Group Sales',
      features: [
        { name: 'Corporate Rate Management', hotelstack: true, competitor: 'Add-On' },
        { name: 'Group Booking with Rooming Lists', hotelstack: true, competitor: 'Add-On' },
        { name: 'AR Aging & Production Reports', hotelstack: true, competitor: 'Add-On' },
        { name: 'Dining Reservations', hotelstack: true, competitor: 'Micros Add-On' },
      ],
    },
    {
      category: 'Staff & HR',
      features: [
        { name: 'Staff Directory & Scheduling', hotelstack: true, competitor: 'Third-Party' },
        { name: 'Payroll Processing', hotelstack: true, competitor: false },
        { name: 'Leave & Attendance', hotelstack: true, competitor: false },
        { name: 'Performance Analytics', hotelstack: true, competitor: false },
      ],
    },
  ],
  pricingComparison: {
    hotelstackStarting: '$49',
    competitorStarting: '$500+',
    hotelstackNote: 'All 36 modules included. No license fees. No server costs. Free onboarding and migration. Regional pricing in EUR, GBP, AED, INR, THB.',
    competitorNote: 'Enterprise licensing from $500+/mo per property. Separate POS, S&C, and OCRM licenses. Implementation fees from $50,000+. On-premise servers required for classic edition.',
  },
  advantages: [
    'Over 90% lower total cost of ownership — no license fees, server costs, or implementation charges',
    'Go live in 48 hours vs 3-6 month OPERA implementation timeline',
    'Modern cloud-native UI accessible from any device — no desktop client needed',
    'All 36 modules included in one subscription vs separate OPERA module licenses',
    'Zero IT infrastructure — no servers, no patches, no Oracle DBA required',
    'Built-in F&B and POS at no extra cost vs separate OPERA POS licensing',
    'Regional pricing in 6 currencies so hotels pay in local currency',
    'Free data migration from OPERA with dedicated support team',
  ],
  verdict: {
    title: 'Our Verdict',
    summary: 'Oracle OPERA has been the industry standard for decades and remains a solid choice for large enterprise chains with existing Oracle infrastructure. However, for the vast majority of hotels, HotelStack.io delivers more modules, better UX, and dramatically lower costs without the multi-month implementation cycles, server overhead, and complex licensing that OPERA demands.',
    bestFor: [
      'Hotels wanting to eliminate expensive Oracle licensing and infrastructure costs',
      'Properties tired of slow OPERA implementation and upgrade cycles',
      'Hotels needing a modern cloud-native UI without desktop client dependencies',
      'SMB and mid-scale properties priced out of OPERA enterprise plans',
      'Properties wanting all modules included without separate license fees',
    ],
  },
  faqItems: [
    { question: 'Can I migrate from Oracle OPERA to HotelStack.io?', answer: 'Yes. Our migration team specializes in OPERA-to-HotelStack transitions. We import guest profiles, reservation history, rate codes, room configurations, and group blocks. Migration is free and typically completes within 48-72 hours with zero downtime.' },
    { question: 'Does HotelStack.io support the same integrations as OPERA?', answer: 'HotelStack.io connects to 100+ OTAs, GDS via Amadeus and Sabre, and major payment gateways. Our integration marketplace covers the same ecosystem. We also provide an open API for custom integrations.' },
    { question: 'How does HotelStack.io compare to OPERA Cloud?', answer: 'OPERA Cloud is Oracle modernizing their legacy product. HotelStack.io was built cloud-native from day one with a modern tech stack. We offer more modules (36 vs OPERA Cloud base), simpler pricing, faster onboarding, and no Oracle infrastructure dependency.' },
    { question: 'Is HotelStack.io suitable for large hotel chains currently on OPERA?', answer: 'Yes. Our Chain and Enterprise plans support multi-property management with centralized analytics, rate distribution, and brand standards — the same capabilities that keep large chains on OPERA, at a fraction of the cost.' },
  ],
};

/* ========================================================================== */
/*  HotelStack.io vs RMS Cloud                                                */
/* ========================================================================== */

const HOTELSTACK_VS_RMS_CLOUD: ComparisonConfig = {
  slug: 'hotelstack-vs-rms-cloud',
  competitorName: 'RMS Cloud',
  heroTitle: 'HotelStack.io vs RMS Cloud',
  heroSubtitle: 'RMS Cloud serves the APAC hospitality market with solid PMS fundamentals. HotelStack.io expands on that with 36 modules, global multi-currency pricing, built-in F&B/POS, and advanced revenue management — all at a lower price point.',
  overviewComparison: [
    { label: 'Total Modules', hotelstack: '36 Integrated Modules', competitor: '12+ Modules' },
    { label: 'Pricing', hotelstack: 'From $49/mo', competitor: 'From $80+/mo' },
    { label: 'Global Reach', hotelstack: '40+ Countries', competitor: 'Primarily APAC' },
    { label: 'F&B / POS', hotelstack: 'Built-In', competitor: 'Not Available' },
    { label: 'Revenue Management', hotelstack: 'Advanced (Built-In)', competitor: 'Basic' },
    { label: 'Booking Engine', hotelstack: 'BookMyRoom.io (Free)', competitor: 'Basic Widget' },
  ],
  featureTable: [
    {
      category: 'AI & Intelligence',
      features: [
        { name: 'AI Dynamic Pricing', hotelstack: true, competitor: false },
        { name: 'AI Demand Forecasting (30/60/90 day)', hotelstack: true, competitor: false },
        { name: 'AI Review Response Generator', hotelstack: true, competitor: false },
        { name: 'AI Guest Concierge (Multilingual)', hotelstack: true, competitor: false },
        { name: 'AI Sentiment Analysis', hotelstack: true, competitor: false },
        { name: 'Predictive Maintenance', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'Core PMS',
      features: [
        { name: 'Cloud-Based PMS', hotelstack: true, competitor: true },
        { name: 'Visual Room Board', hotelstack: true, competitor: true },
        { name: 'Reservation Management', hotelstack: true, competitor: true },
        { name: 'Night Audit Wizard', hotelstack: true, competitor: true },
        { name: 'Group Bookings', hotelstack: true, competitor: true },
        { name: 'Online Check-In Portal', hotelstack: true, competitor: 'Limited' },
      ],
    },
    {
      category: 'Revenue & Distribution',
      features: [
        { name: 'Channel Manager (100+ OTAs)', hotelstack: true, competitor: 'Limited OTAs' },
        { name: 'Dynamic Pricing Rules', hotelstack: true, competitor: false },
        { name: 'Competitor Rate Monitor', hotelstack: true, competitor: false },
        { name: 'What-If Revenue Simulator', hotelstack: true, competitor: false },
        { name: 'Demand Forecasting', hotelstack: true, competitor: 'Basic' },
        { name: 'RevPAR Analytics', hotelstack: true, competitor: 'Basic' },
      ],
    },
    {
      category: 'Guest Management',
      features: [
        { name: '360-Degree Guest Profiles', hotelstack: true, competitor: 'Basic' },
        { name: 'Loyalty Program Management', hotelstack: true, competitor: false },
        { name: 'Guest Communication Automation', hotelstack: true, competitor: 'Limited' },
        { name: 'Guest Spend Analytics', hotelstack: true, competitor: false },
        { name: 'ID Upload & Verification', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'F&B & Operations',
      features: [
        { name: 'POS Terminal', hotelstack: true, competitor: false },
        { name: 'Room Service Module', hotelstack: true, competitor: false },
        { name: 'Kitchen Display System', hotelstack: true, competitor: false },
        { name: 'Housekeeping Management', hotelstack: true, competitor: true },
        { name: 'Maintenance Work Orders', hotelstack: true, competitor: false },
        { name: 'Staff Scheduling & Payroll', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'Multi-Property & Compliance',
      features: [
        { name: 'Chain Dashboard', hotelstack: true, competitor: true },
        { name: 'Cross-Property Analytics', hotelstack: true, competitor: 'Limited' },
        { name: 'Multi-Currency Billing', hotelstack: true, competitor: 'AUD Focus' },
        { name: 'Regional Tax Compliance', hotelstack: true, competitor: 'APAC Only' },
        { name: 'GDPR Data Protection', hotelstack: true, competitor: 'Limited' },
      ],
    },
    {
      category: 'Resilience & Offline',
      features: [
        { name: 'Offline-Capable PWA', hotelstack: true, competitor: false },
        { name: 'Offline Mutation Queue', hotelstack: true, competitor: false },
        { name: 'Background Sync on Reconnect', hotelstack: true, competitor: false },
        { name: 'Conflict-Aware Reconciliation', hotelstack: true, competitor: false },
        { name: 'Installable Front Desk', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'Corporate & Group Sales',
      features: [
        { name: 'Corporate Rate Management', hotelstack: true, competitor: false },
        { name: 'Group Booking with Rooming Lists', hotelstack: true, competitor: 'Limited' },
        { name: 'AR Aging & Production Reports', hotelstack: true, competitor: false },
        { name: 'Dining Reservations', hotelstack: true, competitor: false },
      ],
    },
    {
      category: 'Staff & HR',
      features: [
        { name: 'Staff Directory & Scheduling', hotelstack: true, competitor: false },
        { name: 'Payroll Processing', hotelstack: true, competitor: false },
        { name: 'Leave & Attendance', hotelstack: true, competitor: false },
        { name: 'Performance Analytics', hotelstack: true, competitor: false },
      ],
    },
  ],
  pricingComparison: {
    hotelstackStarting: '$49',
    competitorStarting: '$80+',
    hotelstackNote: 'All 36 modules included. Regional pricing in EUR, GBP, AED, INR, THB. Free onboarding and data migration.',
    competitorNote: 'AUD-based pricing from $80+/mo. Additional charges for channel manager and premium features. Primarily APAC support.',
  },
  advantages: [
    'Nearly triple the modules (36 vs 12+) with all features included at a lower price point',
    'Global coverage across 40+ countries vs RMS Cloud focus on APAC markets',
    'Complete F&B module with POS terminal, room service, and kitchen management',
    'Advanced revenue management with dynamic pricing, forecasting, and simulators',
    'Stronger channel connectivity with 100+ OTAs and GDS via Amadeus and Sabre',
    'Staff management with scheduling, payroll, attendance, and performance tracking',
    'Multi-currency billing in 6 currencies vs primarily AUD-based pricing',
    'Free onboarding and data migration with 24/7 dedicated support',
  ],
  verdict: {
    title: 'Our Verdict',
    summary: 'RMS Cloud is a capable PMS for APAC properties with solid fundamentals for accommodation management. HotelStack.io offers a more comprehensive solution with 36 modules, global reach, advanced revenue management, complete F&B/POS capabilities, plus corporate sales and HR — all at a lower price. For properties looking beyond basic PMS into full hotel operations management, HotelStack.io is the stronger choice.',
    bestFor: [
      'Hotels wanting a complete all-in-one platform beyond basic PMS',
      'Properties needing F&B, POS, and kitchen management built in',
      'Hotels expanding outside APAC needing global compliance and distribution',
      'Properties wanting advanced revenue management and dynamic pricing',
      'Multi-property chains needing centralized analytics across global properties',
    ],
  },
  faqItems: [
    { question: 'Can I migrate from RMS Cloud to HotelStack.io?', answer: 'Yes. Our migration team handles complete data transfer from RMS Cloud including guest profiles, reservation history, rate configurations, and room setups. Migration is free and typically completed within 48 hours with zero downtime.' },
    { question: 'Does HotelStack.io work well for Australian and APAC hotels?', answer: 'Absolutely. HotelStack.io supports multi-currency billing including AUD, regional tax compliance for APAC markets, and connections to APAC-focused OTAs. Many of our APAC properties switched from RMS Cloud for the additional modules and global reach.' },
    { question: 'How does HotelStack.io pricing compare to RMS Cloud?', answer: 'HotelStack.io starts at $49/month with all 36 modules included and regional pricing in 6 currencies. RMS Cloud starts at approximately $80+/month AUD with fewer modules and additional charges for channel management and premium features.' },
    { question: 'Does HotelStack.io support holiday parks and campgrounds like RMS Cloud?', answer: 'HotelStack.io is designed for all accommodation types including hotels, resorts, hostels, boutique properties, and serviced apartments. While RMS Cloud has specific features for holiday parks, HotelStack.io property configuration flexibility supports diverse accommodation models.' },
  ],
};

/* ========================================================================== */
/*  Export maps                                                                */
/* ========================================================================== */

export const COMPARISON_CONFIGS_P2: Record<string, ComparisonConfig> = {
  'hotelstack-vs-opera': HOTELSTACK_VS_OPERA,
  'hotelstack-vs-rms-cloud': HOTELSTACK_VS_RMS_CLOUD,
};

export const COMPARISON_FAQ_P2: Record<string, { question: string; answer: string }[]> = {
  'hotelstack-vs-opera': HOTELSTACK_VS_OPERA.faqItems,
  'hotelstack-vs-rms-cloud': HOTELSTACK_VS_RMS_CLOUD.faqItems,
};
